// diagramFromPrompt — Multi-sheet diagram-authoring skill.
//
// Pipeline:
//   plan_diagram(goal)               → DiagramPlan
//   → for each sheet:
//       create_sheet(name, meta)
//       batch_create_blocks(sheetId, [...])
//       batch_create_connectors(sheetId, [...])
//       create_group(sheetId, [...]) × N
//   → create_cross_sheet_link × M (mellan grannsheets)
//   → validate_diagram
//   → preview + user approval
//
// Denna fil bygger ENDAST ExecutionPlan:en. Dispatcher:n (builder-agent /
// edge-function tool-loop) kör tools:en i ordning.

import { planDiagram, gridPosition } from '@/lib/aiDiagramTools';
import { blocksForSheet, edgesForStyle, type BlockTemplate } from '@/lib/aiSkills/diagramTemplates';
import { registerSkill, type Skill, type SkillContext, type ExecutionPlan, type SkillStep } from '@/lib/aiSkills/skillRegistry';

const TRIGGER_PATTERNS = [
  /arkitekt|architecture/i,
  /kartl[aä]gg|map (?:my|the|our)/i,
  /rita ut|diagram(?:mera)?/i,
  /\bERD\b|schema(?:-| )?diagram/i,
  /(?:module|modul)(?:-| )graph/i,
  /deploy(?:-| )pipeline/i,
  /multi(?:-| )sheet/i,
];

function matchesDiagramIntent(prompt: string): boolean {
  return TRIGGER_PATTERNS.some(re => re.test(prompt));
}

/**
 * Estimera credits baserat på planens storlek. Tokens scaling ~= 2× antal
 * blocks + 0.5× antal connectors (rough ballpark; fine-tuned när token-meter
 * har empiri).
 */
function estimateCost(sheetCount: number, blocksPerSheet: number): number {
  const connectorsPerSheet = Math.max(1, blocksPerSheet - 1);
  const perSheet = 50 + blocksPerSheet * 2 + connectorsPerSheet * 0.5;
  return Math.ceil(sheetCount * perSheet);
}

/**
 * Bygg ExecutionPlan för en "skapa multi-sheet-diagram från prompt"-request.
 * Planen är deterministisk (samma input → samma plan) så UI kan preview:a
 * den innan user godkänner credit-kostnaden.
 */
export function planDiagramFromPrompt(ctx: SkillContext): ExecutionPlan {
  const plan = planDiagram(ctx.userPrompt);
  const steps: SkillStep[] = [];
  let idx = 0;

  steps.push({
    index: idx++,
    tool: 'plan_diagram',
    args: { goal: ctx.userPrompt },
    description: `Planera ${plan.sheetCount} sheets (${plan.style}-stil)`,
  });

  // Z2: Sheet-prefix gör block-refs unika över sheets. blocksForSheet
  // returnerar style-specifika templates (Entry/Router/DB osv) istället
  // för generic "Node 1, Node 2, ...". edgesForStyle bygger connector-
  // mönstret (linjär kedja för pipeline/architecture, hub→spokes för
  // hub-spoke).
  const refForSheet = (sheetIdx: number) => `sheet-${sheetIdx}`;
  const sheetTemplates = plan.sheets.map((sheet) => ({
    sheet,
    templates: blocksForSheet(sheet.index, plan.style, sheet.name),
  }));
  // Föredra style-templates men respektera ev. estimatedBlocks-min.
  const blocksPerSheet = sheetTemplates[0]?.templates.length ?? 6;

  for (const { sheet, templates } of sheetTemplates) {
    steps.push({
      index: idx++,
      tool: 'create_sheet',
      args: {
        sheet_ref: refForSheet(sheet.index),
        name: sheet.name,
        color: sheet.color,
        description: sheet.description ?? `Sheet ${sheet.index + 1} of ${plan.sheetCount}`,
      },
      description: `Skapa sheet "${sheet.name}"`,
    });

    const sheetRef = refForSheet(sheet.index);
    // Sheet-prefix block-refs så två sheets kan ha samma template-ref
    // utan kollision.
    const blocks = templates.map((t: BlockTemplate, i) => {
      const pos = gridPosition(i);
      return {
        ref: `${sheetRef}-${t.ref}`,
        type: t.type,
        x: pos.x,
        y: pos.y,
        config: {
          name: t.name,
          ...(t.description ? { description: t.description } : {}),
          ...(t.icon ? { icon: t.icon } : {}),
        },
        color: sheet.color,
      };
    });
    steps.push({
      index: idx++,
      tool: 'batch_create_blocks',
      args: { sheet_ref: sheetRef, blocks },
      description: `Lägg till ${blocks.length} blocks på sheet ${sheet.index + 1}`,
    });

    // Style-specifika connectors. edgesForStyle använder template-refs
    // utan sheet-prefix; map:a om till de prefix:ade refs.
    const styleEdges = edgesForStyle(plan.style, templates);
    const edges = styleEdges.map((e) => ({
      fromRef: `${sheetRef}-${e.fromRef}`,
      toRef: `${sheetRef}-${e.toRef}`,
      ...(e.label ? { label: e.label } : {}),
    }));
    steps.push({
      index: idx++,
      tool: 'batch_create_connectors',
      args: { sheet_ref: sheetRef, edges },
      description: `Koppla ${edges.length} connectors på sheet ${sheet.index + 1}`,
    });
  }

  // Cross-sheet links mellan grannsheets (sista block på sheet N → första
  // block på sheet N+1). Använder första template-ref:en eftersom det är
  // den meningsfulla "entry-noden".
  for (let i = 0; i < plan.sheets.length - 1; i++) {
    const fromTemplates = sheetTemplates[i].templates;
    const toTemplates = sheetTemplates[i + 1].templates;
    const fromLast = fromTemplates[fromTemplates.length - 1]?.ref;
    const toFirst = toTemplates[0]?.ref;
    if (!fromLast || !toFirst) continue;
    steps.push({
      index: idx++,
      tool: 'create_cross_sheet_link',
      args: {
        from_sheet_index: i,
        to_sheet_index: i + 1,
        from_block_ref: `${refForSheet(i)}-${fromLast}`,
        to_block_ref: `${refForSheet(i + 1)}-${toFirst}`,
        label: 'next',
      },
      description: `Länka sheet ${i + 1} → ${i + 2}`,
    });
  }

  steps.push({
    index: idx++,
    tool: 'validate_diagram',
    args: {},
    description: 'Kör invariant-check',
  });

  return {
    skill: 'diagramFromPrompt',
    goal: ctx.userPrompt,
    steps,
    estimatedCost: estimateCost(plan.sheetCount, blocksPerSheet),
  };
}

const skill: Skill = {
  name: 'diagramFromPrompt',
  description:
    'Bygg ett fullt multi-sheet-diagram från en natural-language-prompt. Plan → create sheets → batch blocks → batch connectors → cross-sheet links → validate → preview.',
  trigger: matchesDiagramIntent,
  plan: planDiagramFromPrompt,
};

registerSkill(skill);

export default skill;
